import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Loader2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

// field: [name, type, label]  types: text, number, textarea, image, list, bool, select:opt|opt
const SCHEMAS = {
  services: { key: "slug", label: "Service", fields: [
    ["title", "text", "Title"], ["slug", "text", "Slug"], ["category", "text", "Category"], ["icon", "text", "Icon name (lucide)"],
    ["short_desc", "text", "Short description"], ["description", "textarea", "Full description"], ["starting_price", "number", "Starting price"],
    ["image", "image", "Image URL"], ["order", "number", "Order"], ["features", "list", "Features"], ["published", "bool", "Published"]] },
  packages: { key: "id", label: "Package", fields: [
    ["name", "text", "Name"], ["tagline", "text", "Tagline"], ["price", "number", "Price (0 = custom)"], ["period", "text", "Period"],
    ["cta", "text", "CTA label"], ["popular", "bool", "Most popular"], ["order", "number", "Order"], ["features", "list", "Features"], ["published", "bool", "Published"]] },
  portfolio: { key: "slug", label: "Portfolio item", fields: [
    ["title", "text", "Title"], ["slug", "text", "Slug"], ["category", "text", "Category"], ["client", "text", "Client"],
    ["summary", "textarea", "Summary"], ["image", "image", "Image URL"], ["tags", "list", "Tags"], ["results", "list", "Results"],
    ["is_case_study", "bool", "Is case study"], ["order", "number", "Order"], ["published", "bool", "Published"]] },
  testimonials: { key: "id", label: "Testimonial", fields: [
    ["name", "text", "Name"], ["role", "text", "Role"], ["company", "text", "Company"], ["quote", "textarea", "Quote"],
    ["rating", "number", "Rating (1-5)"], ["order", "number", "Order"], ["published", "bool", "Published"]] },
  faqs: { key: "id", label: "FAQ", fields: [
    ["question", "text", "Question"], ["answer", "textarea", "Answer"], ["category", "text", "Category"], ["order", "number", "Order"], ["published", "bool", "Published"]] },
  blog: { key: "slug", label: "Blog post", fields: [
    ["title", "text", "Title"], ["slug", "text", "Slug"], ["category", "text", "Category"], ["excerpt", "textarea", "Excerpt"],
    ["content", "textarea", "Content"], ["cover_image", "image", "Cover image URL"], ["tags", "list", "Tags"], ["author", "text", "Author"],
    ["status", "select:draft|published", "Status"]] },
};

export default function AdminContent({ kind, title }) {
  const schema = SCHEMAS[kind];
  const qc = useQueryClient();
  const { data: items = [] } = useQuery({ queryKey: ["admin-content", kind], queryFn: async () => (await api.get(`/admin/content/${kind}`)).data });
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);

  const openNew = () => { setEditing(null); setForm({ published: true }); setOpen(true); };
  const openEdit = (item) => { setEditing(item); setForm({ ...item }); setOpen(true); };
  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    const payload = { ...form };
    schema.fields.forEach(([name, type]) => {
      if (type === "number" && payload[name] !== undefined && payload[name] !== "") payload[name] = Number(payload[name]);
    });
    try {
      if (editing) await api.put(`/admin/content/${kind}/${editing[schema.key]}`, payload);
      else await api.post(`/admin/content/${kind}`, payload);
      toast.success(`${schema.label} saved`);
      setOpen(false);
      qc.invalidateQueries({ queryKey: ["admin-content", kind] });
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const del = async (item) => {
    if (!window.confirm(`Delete this ${schema.label.toLowerCase()}?`)) return;
    try { await api.delete(`/admin/content/${kind}/${item[schema.key]}`); toast.success("Deleted"); qc.invalidateQueries({ queryKey: ["admin-content", kind] }); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const primary = (item) => item.title || item.name || item.question || item.subject || "Untitled";

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div><h1 className="font-display text-2xl font-bold">{title}</h1><p className="text-sm text-muted-foreground">{items.length} items · fully editable</p></div>
        <Button className="rounded-full" onClick={openNew} data-testid={`${kind}-new`}><Plus className="mr-2 h-4 w-4" /> Add {schema.label.toLowerCase()}</Button>
      </div>

      <div className="mt-6 grid gap-3">
        {items.map((item) => (
          <div key={item[schema.key] || item.id} className="flex items-center justify-between gap-4 bs-card p-4" data-testid={`${kind}-item`}>
            <div className="flex min-w-0 items-center gap-3">
              {(item.image || item.cover_image) && <img src={item.image || item.cover_image} alt="" className="hidden h-12 w-16 shrink-0 rounded-lg object-cover sm:block" />}
              <div className="min-w-0">
                <p className="truncate font-display font-bold">{primary(item)}</p>
                <div className="mt-1 flex flex-wrap gap-1.5">
                  {item.category && <Badge variant="outline">{item.category}</Badge>}
                  {item.status && <Badge variant="secondary">{item.status}</Badge>}
                  {typeof item.published === "boolean" && <Badge className={item.published ? "bg-success/15 text-success" : "bg-muted text-muted-foreground"}>{item.published ? "Published" : "Draft"}</Badge>}
                  {item.popular && <Badge>Popular</Badge>}
                </div>
              </div>
            </div>
            <div className="flex shrink-0 gap-1">
              <Button variant="ghost" size="icon" onClick={() => openEdit(item)} data-testid={`${kind}-edit`}><Pencil className="h-4 w-4" /></Button>
              <Button variant="ghost" size="icon" onClick={() => del(item)} data-testid={`${kind}-delete`}><Trash2 className="h-4 w-4 text-destructive" /></Button>
            </div>
          </div>
        ))}
        {items.length === 0 && <p className="rounded-xl border border-dashed border-border p-8 text-center text-sm text-muted-foreground">No items yet.</p>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
          <DialogHeader><DialogTitle>{editing ? "Edit" : "New"} {schema.label.toLowerCase()}</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-3.5" data-testid={`${kind}-form`}>
            {schema.fields.map(([name, type, label]) => (
              <div key={name}>
                <Label className="text-xs">{label}</Label>
                {type === "textarea" ? (
                  <Textarea rows={name === "content" ? 6 : 3} value={form[name] ?? ""} onChange={(e) => upd(name, e.target.value)} className="mt-1" />
                ) : type === "bool" ? (
                  <div className="mt-1.5"><Switch checked={!!form[name]} onCheckedChange={(v) => upd(name, v)} /></div>
                ) : type === "list" ? (
                  <Textarea rows={2} value={Array.isArray(form[name]) ? form[name].join("\n") : (form[name] ?? "")}
                    onChange={(e) => upd(name, e.target.value.split("\n").map((s) => s.trim()).filter(Boolean))}
                    placeholder="One per line" className="mt-1" />
                ) : type.startsWith("select:") ? (
                  <Select value={form[name] ?? ""} onValueChange={(v) => upd(name, v)}>
                    <SelectTrigger className="mt-1"><SelectValue placeholder="Choose" /></SelectTrigger>
                    <SelectContent>{type.split(":")[1].split("|").map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                  </Select>
                ) : (
                  <Input type={type === "number" ? "number" : "text"} value={form[name] ?? ""} onChange={(e) => upd(name, e.target.value)} className="mt-1" />
                )}
              </div>
            ))}
            <DialogFooter>
              <Button type="submit" className="rounded-full" disabled={busy} data-testid={`${kind}-save`}>{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Save</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
